import { useContext } from 'react';
import { ReportConversationModal } from '@zg-rentals/satellite-components';
import useModal from '../../hooks/useModal';
import useLeadManagementActions from '../../hooks/useLeadManagementActions';
import { ModalType } from '../../../src/types/ModalType';
import { useCachedLeadByConversationId } from '../../hooks/useLeadManagementTableQuery';
import ContactInfoCallModal from '../Modals/ContactInfoCallModal';
import RequestRenterProfileModal from '../Modals/RequestRenterProfileModal';
import RenterDetailsModal from '../Modals/RenterDetailsModal';
import ApplicationsUpsellModal from '../Modals/ApplicationsUpsellModal';
import LeaseOptionsModal from '../Modals/LeaseOptionsModal';
import { LeadManagementContext } from '../../contexts/LeadManagementContextProvider';
import { AppContext } from '../../contexts/AppContextProvider';
import useQueryParamsNext from '../../hooks/useQueryParamsNext';
import ApproveLeadModal from '../Modals/ApproveLeadModal';

type LeadManagementModalsProps = {
  activeRowId: string;
  setActiveRowId: (conversationId: string) => void;
};

export default function LeadManagementModals({ activeRowId, setActiveRowId }: LeadManagementModalsProps) {
  const { isImpersonator } = useContext(AppContext);
  const { conversationId } = useContext(LeadManagementContext);
  const { activeModal, clearModal, setActiveModal } = useModal();
  const activeConversationId = activeRowId || conversationId;
  const { handleReportClick, handleRequestRenterProfile, hasErrorOnRequestRenterProfile } = useLeadManagementActions({
    conversationId: activeConversationId,
  });
  const { updateQueryParams } = useQueryParamsNext();

  const leadForActiveRow = useCachedLeadByConversationId(activeConversationId);

  const {
    leadId,
    renterName: renterNameForActiveRow = '',
    renterPhoneNumber: renterPhoneNumberForActiveRow = '',
  } = leadForActiveRow?.renterInfo || {};

  // Listing identity comes from the active row's lead, not page-level context (the first lead's
  // under multi-listing selection), so the lease/approve/upsell modals target the right listing.
  const activeLeadListingAlias = leadForActiveRow?.listingDetails?.listingAlias ?? '';
  const activeLeadStreetAddress = leadForActiveRow?.listingDetails?.address?.streetAddress ?? '';
  const handleClearActiveRowId = () => setActiveRowId('');

  const handleModalClose = () => {
    clearModal();
    handleClearActiveRowId();
    updateQueryParams({
      queryParamValues: {
        conversationId: null,
      },
      shouldReplaceHistory: true,
    });
  };

  const handleModalCancel = () => {
    // Re-open the renter details modal if the conversationId is still in the url
    return conversationId ? setActiveModal(ModalType.RenterDetailsModal) : handleModalClose();
  };

  return (
    <>
      {(activeModal === ModalType.RenterDetailsModal || Boolean(conversationId)) && <RenterDetailsModal />}

      {activeModal === ModalType.ApplicationsUpsellModal && (
        <ApplicationsUpsellModal listingAlias={activeLeadListingAlias} />
      )}

      {activeModal === ModalType.ContactInfoCallModal && (
        <ContactInfoCallModal
          name={renterNameForActiveRow}
          phone={renterPhoneNumberForActiveRow}
          onCallCancelClick={handleModalCancel}
        />
      )}

      {activeModal === ModalType.ReportConversationModal && (
        <ReportConversationModal
          isImpersonator={isImpersonator}
          name={renterNameForActiveRow}
          onCancelClick={handleModalCancel}
          onReportClick={({ reason, comments }) => {
            handleModalClose();
            handleReportClick({
              reportReasonType: reason,
              userReportComment: comments,
            });
          }}
        />
      )}

      {activeModal === ModalType.RequestRenterProfileModal && (
        <RequestRenterProfileModal
          name={renterNameForActiveRow}
          onRequestRenterProfileClick={() => handleRequestRenterProfile(handleModalClose)}
          hasError={hasErrorOnRequestRenterProfile}
          onClose={handleClearActiveRowId}
        />
      )}

      {activeModal === ModalType.LeaseOptionsModal && (
        <LeaseOptionsModal
          conversationId={activeConversationId}
          renterName={renterNameForActiveRow}
          streetAddress={activeLeadStreetAddress}
          listingAlias={activeLeadListingAlias}
          leadId={leadId}
          onClose={handleModalCancel}
        />
      )}

      {activeModal === ModalType.ApproveLeadModal && (
        <ApproveLeadModal
          conversationId={activeConversationId}
          name={renterNameForActiveRow}
          streetAddress={activeLeadStreetAddress}
          listingAlias={activeLeadListingAlias}
          leadId={leadId}
          onClose={handleModalCancel}
        />
      )}
    </>
  );
}
